import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Navigation from '../main-page/Navigation';
import AuthButtons from '../main-page/AuthButtons';
import Footer from '../main-page/Footer';
import BottomNav from '../main-page/BottomNav';
import NotificationBell from '../notifications/NotificationBell';
import IslamicRibbon from '../common/IslamicRibbon';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MOBILE_ADMIN_ITEMS,
  MOBILE_DISCOVER_ITEMS,
  MOBILE_PRIMARY_ITEMS,
  getVisibleMobileItems,
  isMobileNavItemActive,
} from '../main-page/mobileNavigation';
import { navigateToHome } from '../../utils/homeNavigation';

const PageLayout = ({ children, title = "PakPoints", onNavigate, onMobileBack }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState('primary');

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const handleProfileClick = () => {
    navigate('/?auth=profile');
  };

  const isMobileActive = (page) => isMobileNavItemActive(location, page);

  const getMobileButtonClasses = (page) => {
    const baseClasses = "block w-full text-left px-3 py-2 text-base font-medium rounded-md relative";
    if (isMobileActive(page)) {
      return `${baseClasses} text-blue-600 bg-blue-50 font-semibold border-b-2 border-blue-600`;
    }
    return `${baseClasses} text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800`;
  };

  const handleMobileNavigation = (page) => {
    if (page === 'home') navigateToHome(navigate, onNavigate);
    else if (page === 'explore') navigate('/explore');
    else if (page === 'islam') navigate('/islam');
    else if (page === 'societies') navigate('/societies');
    else if (page === 'institutes') navigate('/institutes');
    else if (page === 'businesses') navigate('/businesses');
    else if (page === 'events') navigate('/events');
    else if (page === 'ads') navigate('/ads');
    else if (page === 'points') navigate('/points');
    else if (page === 'posts') navigate('/posts');
    else if (page === 'properties') navigate('/properties');
    else if (page === 'turfs') navigate('/turfs');
    else if (page === 'contact') navigate('/contact');
    else if (page === 'messages') navigate('/messages');
    else if (page === 'scrapper') navigate('/scrapper');
    else if (page === 'admin') navigate('/admin-portal');
    else if (page === 'profile') navigate('/?auth=profile');
    else if (page === 'login') navigate('/?auth=login');
    else if (page === 'register') navigate('/?auth=register');
    else if (onNavigate) onNavigate(page);
    else navigate('/');
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const hasActivePrimaryItem = MOBILE_PRIMARY_ITEMS.some((item) => isMobileActive(item.id));
    const hasActiveDiscoverItem = MOBILE_DISCOVER_ITEMS.some((item) => isMobileActive(item.id));
    const hasActiveAdminItem = MOBILE_ADMIN_ITEMS.some((item) => isMobileActive(item.id));
    if (hasActivePrimaryItem) setExpandedSection('primary');
    else if (hasActiveDiscoverItem) setExpandedSection('discover');
    else if (hasActiveAdminItem) setExpandedSection('admin');
    else setExpandedSection('primary');
  }, [isMobileMenuOpen, location.pathname, location.search]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <header className="sticky top-0 z-50 bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <IslamicRibbon />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4 sm:py-6">
            {/* Mobile back + Logo - Far Left */}
            <div className="flex items-center gap-0 sm:gap-1 flex-shrink-0 min-w-0">
              {typeof onMobileBack === 'function' && (
                <button
                  type="button"
                  onClick={onMobileBack}
                  className="md:hidden flex-shrink-0 p-2 -ml-2 mr-0.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md"
                  aria-label="Go back"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="flex-shrink-0">
                <button
                  type="button"
                  onClick={() => navigateToHome(navigate, onNavigate)}
                  className="hover:opacity-80 transition-opacity"
                >
                  <img 
                    src="/images/logo.svg" 
                    alt="PakPoints Logo" 
                    className="h-8 w-8 sm:h-10 sm:w-10"
                  />
                </button>
              </div>
            </div>
            
            {/* Desktop Navigation - Center */}
            <div className="hidden md:flex flex-1 justify-center">
              <Navigation onNavigate={(page) => {
                if (page === 'main') {
                  navigateToHome(navigate, onNavigate);
                }
                // Other navigation handled by router
              }} />
            </div>
            
            {/* Desktop Auth Section - Far Right. Messages available for any logged-in user. */}
            <div className="hidden md:flex items-center space-x-4 flex-shrink-0">
              {isAuthenticated ? (
                <>
                  <NotificationBell />
                  <button
                    type="button"
                    onClick={() => navigate('/messages')}
                    className="text-blue-600 hover:text-blue-800 font-medium transition-colors flex items-center"
                    aria-label="Messages"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </button>
                  <div className="text-gray-700">
                    <button
                      onClick={handleProfileClick}
                      className="text-blue-600 hover:text-blue-800 font-medium underline transition-colors flex items-center space-x-1"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                      <span className="hidden lg:inline">{user?.full_name || user?.first_name || user?.email || 'Account'}</span>
                    </button>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <AuthButtons />
              )}
            </div>

            {/* Mobile Menu Button - Far Right on Mobile */}
            <div className="md:hidden flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 p-2"
                aria-expanded={isMobileMenuOpen}
                aria-controls="page-layout-mobile-menu"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div
              id="page-layout-mobile-menu"
              className="md:hidden border-t border-gray-200 dark:border-gray-700 py-4 bg-white dark:bg-gray-900"
            >
              <div className="space-y-4">
                <div>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-3 mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase"
                    onClick={() => setExpandedSection((prev) => (prev === 'primary' ? null : 'primary'))}
                    aria-expanded={expandedSection === 'primary'}
                    aria-controls="mobile-primary-section-layout"
                  >
                    <span>Primary</span>
                    <span className="text-sm text-gray-500" aria-hidden>
                      {expandedSection === 'primary' ? '−' : '+'}
                    </span>
                  </button>
                  {expandedSection === 'primary' && (
                    <div id="mobile-primary-section-layout" className="space-y-1">
                      {getVisibleMobileItems(MOBILE_PRIMARY_ITEMS, { isAuthenticated, user }).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleMobileNavigation(item.id)}
                          className={getMobileButtonClasses(item.id)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-3 mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase"
                    onClick={() => setExpandedSection((prev) => (prev === 'discover' ? null : 'discover'))}
                    aria-expanded={expandedSection === 'discover'}
                    aria-controls="mobile-discover-section-layout"
                  >
                    <span>Discover</span>
                    <span className="text-sm text-gray-500" aria-hidden>
                      {expandedSection === 'discover' ? '−' : '+'}
                    </span>
                  </button>
                  {expandedSection === 'discover' && (
                    <div id="mobile-discover-section-layout" className="space-y-1">
                      {getVisibleMobileItems(MOBILE_DISCOVER_ITEMS, { isAuthenticated, user }).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleMobileNavigation(item.id)}
                          className={getMobileButtonClasses(item.id)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {getVisibleMobileItems(MOBILE_ADMIN_ITEMS, { isAuthenticated, user }).length > 0 && (
                  <div>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-3 mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase"
                      onClick={() => setExpandedSection((prev) => (prev === 'admin' ? null : 'admin'))}
                      aria-expanded={expandedSection === 'admin'}
                      aria-controls="mobile-admin-section-layout"
                    >
                      <span>Admin</span>
                      <span className="text-sm text-gray-500" aria-hidden>
                        {expandedSection === 'admin' ? '−' : '+'}
                      </span>
                    </button>
                    {expandedSection === 'admin' && (
                      <div id="mobile-admin-section-layout" className="space-y-1">
                        {getVisibleMobileItems(MOBILE_ADMIN_ITEMS, { isAuthenticated, user }).map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => handleMobileNavigation(item.id)}
                            className={getMobileButtonClasses(item.id)}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-3 mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase"
                    onClick={() => setExpandedSection((prev) => (prev === 'profile' ? null : 'profile'))}
                    aria-expanded={expandedSection === 'profile'}
                    aria-controls="mobile-profile-section-layout"
                  >
                    <span>Profile</span>
                    <span className="text-sm text-gray-500" aria-hidden>
                      {expandedSection === 'profile' ? '−' : '+'}
                    </span>
                  </button>
                  {expandedSection === 'profile' && (
                    <>
                      {isAuthenticated ? (
                        <div id="mobile-profile-section-layout" className="space-y-3">
                          <div className="px-3 py-2">
                            <p className="text-sm font-medium text-gray-900">
                              {user?.full_name || user?.first_name || user?.email || 'Account'}
                            </p>
                          </div>
                          <div className="px-3 py-2">
                            <NotificationBell />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleMobileNavigation('messages')}
                            className="block w-full text-left px-3 py-2 text-base font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md flex items-center gap-2"
                            aria-label="Messages"
                          >
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            Messages
                          </button>
                          <button
                            onClick={handleProfileClick}
                            className="block w-full text-left px-3 py-2 text-base font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                          >
                            Profile
                          </button>
                          <button
                            onClick={handleLogout}
                            className="block w-full text-left px-3 py-2 text-base font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                          >
                            Logout
                          </button>
                        </div>
                      ) : (
                        <div id="mobile-profile-section-layout" className="space-y-2">
                          <button
                            onClick={() => {
                              navigate('/?auth=login');
                              setIsMobileMenuOpen(false);
                            }}
                            className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                          >
                            Login
                          </button>
                          <button
                            onClick={() => {
                              navigate('/?auth=register');
                              setIsMobileMenuOpen(false);
                            }}
                            className="block w-full text-left px-3 py-2 text-base font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                          >
                            Sign Up
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>
      
      <main className="flex-1 pb-16 md:pb-0">
        {children}
      </main>
      
      <Footer onNavigate={(page) => {
        if (page === 'main') {
          navigate('/');
        }
        // Other navigation handled by router
      }} />
      <BottomNav />
    </div>
  );
};

export default PageLayout;
